/**
 * Draggable before/after slider overlay for AI enhancement preview.
 * Shown in the Viewer when an enhancement is being evaluated.
 */

import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './EnhancePreview.module.css';

interface EnhancePreviewProps {
  originalDataUrl: string;
  enhancedDataUrl: string;
  prompt: string;
  onAccept: () => void;
  onRetry: (editedPrompt: string) => void;
  onDismiss: () => void;
}

export function EnhancePreview({
  originalDataUrl,
  enhancedDataUrl,
  prompt,
  onAccept,
  onRetry,
  onDismiss,
}: EnhancePreviewProps) {
  const { t } = useTranslation('viewer');
  const [splitPct, setSplitPct] = useState(50);
  const [editedPrompt, setEditedPrompt] = useState(prompt);
  const sliderRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);

  const updateSplit = useCallback((clientX: number) => {
    const el = sliderRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setSplitPct(Math.max(5, Math.min(95, pct)));
  }, []);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    draggingRef.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    updateSplit(e.clientX);
  }, [updateSplit]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!draggingRef.current) return;
    updateSplit(e.clientX);
  }, [updateSplit]);

  const onPointerUp = useCallback(() => {
    draggingRef.current = false;
  }, []);

  return (
    <div className={styles.overlay}>
      <div
        ref={sliderRef}
        className={styles.sliderArea}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {/* Before (original) — full width, visible on left of divider */}
        <img
          className={styles.sliderImage}
          src={originalDataUrl}
          alt={t('originalImageAlt')}
          draggable={false}
        />
        {/* After (enhanced) — clipped to right of divider */}
        <img
          className={`${styles.sliderImage} ${styles.afterClip}`}
          src={enhancedDataUrl}
          alt={t('enhancedImageAlt')}
          draggable={false}
          style={{ '--split-pct': `${splitPct}%` } as React.CSSProperties}
        />
        {/* Divider line + handle */}
        <div className={styles.dividerLine} style={{ left: `${splitPct}%` }}>
          <div className={styles.dividerHandle}>
            <svg viewBox="0 0 24 24"><path d="M8 5l-5 7 5 7M16 5l5 7-5 7" /></svg>
          </div>
        </div>
        {/* Labels */}
        <span className={styles.beforeLabel}>{t('beforeLabel')}</span>
        <span className={styles.afterLabel}>{t('afterLabel')}</span>
      </div>

      <div className={styles.bottomBar}>
        <input
          className={styles.promptInput}
          value={editedPrompt}
          onChange={e => setEditedPrompt(e.target.value)}
          placeholder={t('enhancementPromptPlaceholder')}
          title={t('enhancementPromptTooltip')}
        />
        <button
          className={`${styles.actionBtn} ${styles.acceptBtn}`}
          onClick={onAccept}
          title={t('acceptEnhancementTooltip')}
        >
          &#x2713;
        </button>
        <button
          className={`${styles.actionBtn} ${styles.retryBtn}`}
          onClick={() => onRetry(editedPrompt)}
          title={t('retryEnhancementTooltip')}
        >
          &#x21BB;
        </button>
        <button
          className={`${styles.actionBtn} ${styles.dismissBtn}`}
          onClick={onDismiss}
          title={t('dismissEnhancementTooltip')}
        >
          &#x2715;
        </button>
      </div>
    </div>
  );
}
